// @file All network requests used by payment plan store
import { fetchJson } from '@@/surface/api_fetch'
import type {
  AdvertisedQuotasApiResult,
  EstimateCancellationRefundResult,
  EstimateSwitchingCostResult,
  LibraryId,
  LibraryPlan as LibraryPlanType,
  Plan as PlanType,
  RedeemLicenseKeyResponse,
  ScheduledChangesResult,
  StripeAddress,
  StripeInvoiceDetails,
  SwitchCurrencyResult,
  SwitchPlanResult,
  UndoScheduledChangeResult,
} from '@@/types'
import type { Billing as BillingType, JsonAPIResponse } from '@padlet/arvo'
import { fetchBilling } from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'

// Ensure this version is synced with `mozart/tests/playwright/helpers/dashboard_test.ts`
const API_VERSION = 5

class Billing {
  public static async fetch(): Promise<JsonAPIResponse<BillingType>> {
    return await fetchBilling()
  }

  /**
   * Fetch estimated refund.
   */
  public static async estimateCancellationRefund(
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateCancellationRefundResult>> {
    return await fetchJson(`/premium/cancel/refund_estimate`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async undoScheduledCancellation(fetchOptions = {}): Promise<UndoScheduledChangeResult> {
    return await fetchJson('/premium/undo_scheduled_cancellation', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async removePaymentMethod(fetchOptions = {}): Promise<PaymentMethodRemovalResult> {
    return await fetchJson('/premium/payment-method/remove', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async undoScheduledSwitch(fetchOptions = {}): Promise<UndoScheduledChangeResult> {
    return await fetchJson('/premium/undo_scheduled_plan_switch', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async estimateSwitchingCost(fetchOptions = {}): Promise<EstimateSwitchingCostResult> {
    return await fetchJson('/premium/switch_plan/estimate_cost', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchPlan(fetchOptions = {}): Promise<SwitchPlanResult> {
    return await fetchJson('/premium/switch_plan', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchCurrency(fetchOptions = {}): Promise<SwitchCurrencyResult> {
    return await fetchJson('/premium/switch_currency', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async cancelMembership(fetchOptions = {}): Promise<SwitchPlanResult> {
    return await fetchJson('/premium/cancel', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async fetchScheduledChanges(): Promise<ScheduledChangesResult> {
    return await fetchJson('/api/3/billing/scheduled_changes', {
      method: HTTPMethod.get,
    })
  }
}

interface PaymentMethodRemovalResult {
  success: boolean
  partial: boolean
}

class Checkout {
  public static async fetchCheckoutPageUrl(
    body: { nextUrl: string; priceId: string; libraryId?: string },
    fetchOptions = {},
  ): Promise<{ checkoutPageUrl: string }> {
    return await fetchJson('/api/1/premium/checkout/init', {
      method: HTTPMethod.post,
      jsonData: body,
      ...fetchOptions,
    })
  }
}

class StripePaypal {
  public static async start(
    body: { address: StripeAddress; customer?: string; token: string },
    fetchOptions = {},
  ): Promise<{ data: { attributes: { redirect_url: string } } }> {
    return await fetchJson('/api/1/billing/stripe-paypal/start', {
      method: HTTPMethod.post,
      jsonData: body,
      ...fetchOptions,
    })
  }
}

class Stripe {
  public static async createSetupIntent(
    body: { address: StripeAddress; customer?: string; token: string },
    fetchOptions = {},
  ): Promise<{ data: { attributes: { client_secret: string } } }> {
    return await fetchJson('api/1/billing/stripe/setup-intent', {
      method: HTTPMethod.post,
      jsonData: body,
      ...fetchOptions,
    })
  }

  public static async createSubscription(
    body: { address: StripeAddress; customer?: string; token: string; tax_id?: { type: string; value: string } },
    fetchOptions = {},
  ): Promise<{ data: { attributes: { type: string; client_secret: string; subscription_id: string } } }> {
    return await fetchJson('api/1/billing/stripe/subscription', {
      method: HTTPMethod.post,
      jsonData: body,
      ...fetchOptions,
    })
  }

  public static async cancelSubscription(
    body: { subscription_id: string; token: string },
    fetchOptions = {},
  ): Promise<{ data: { success: boolean; error?: string } }> {
    return await fetchJson('api/1/billing/stripe/cancel', {
      method: HTTPMethod.post,
      jsonData: body,
      ...fetchOptions,
    })
  }

  public static async calculatePriceForAddress(
    body: { address: StripeAddress; token: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<StripeInvoiceDetails>> {
    return await fetchJson('api/1/billing/stripe/calculate-price', {
      method: HTTPMethod.post,
      jsonData: body,
      ...fetchOptions,
    })
  }

  public static async redeemLicenseKey(
    body: { license_key: string; confirm?: boolean; accept_no_refund?: boolean },
    fetchOptions = {},
  ): Promise<RedeemLicenseKeyResponse> {
    return await fetchJson('api/1/billing/stripe/redeem-license-key', {
      method: HTTPMethod.post,
      jsonData: body,
      ...fetchOptions,
    })
  }
}

class Plan {
  /**
   * Fetch list of available plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<PlanType>> {
    return await fetchJson(`/api/${API_VERSION}/plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchAdvertisedQuotas(fetchOptions = {}): Promise<AdvertisedQuotasApiResult> {
    return await fetchJson('/api/3/membership_tier_quotas', {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class Ab {
  public static async convertAbExperimentGoal(
    experiment_name: string,
    conversion_goal: string,
  ): Promise<JsonAPIResponse<any>> {
    return await fetchJson('/api/1/ab-test/convert', {
      method: HTTPMethod.post,
      jsonData: {
        experiment_name,
        conversion_goal,
      },
    })
  }

  public static async convertPersonalPricingPlansExperiment(): Promise<JsonAPIResponse<any>> {
    return await fetchJson('/api/1/ab-test/pricing-plans/convert', {
      method: HTTPMethod.post,
    })
  }
}

class LibraryPlan {
  /**
   * Fetch list of available library plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<LibraryPlanType>> {
    return await fetchJson(`/api/${API_VERSION}/team_plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Fetch list of available library plans for the current user and a specific library,
   * based on the user's signup country and the library's type.
   *
   * Returns team plans for team libraries, and classroom plans for classroom libraries.
   *
   * Does not return school libraries because school plans are not self-serve.
   * School library users have to contact Customer Success to subscribe instead.
   */
  public static async fetchByLibraryId(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryPlanType>> {
    return await fetchJson(`/api/${API_VERSION}/library_plans/${libraryId}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

export { Ab, Billing, Checkout, LibraryPlan, Plan, Stripe, StripePaypal }
